import { create } from 'zustand';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
  type AuthProvider,
  type User,
} from 'firebase/auth';
import { AGE_GATES, CONSENT_VERSIONS } from '@erectus/shared';
import type { UserDoc } from '@erectus/shared';
import { appleProvider, firebase, googleProvider } from '@/lib/firebase.js';
import { getUser } from '@/lib/repo.js';
import { setProps } from '@/lib/analytics.js';
import { isNative, platform } from '@/lib/device.js';
import { forgetPushToken, refreshPushToken } from '@/lib/reminders.js';
import { readEnv } from '@/lib/env.js';
import { setCrashUser } from '@/lib/crashlytics.js';
import { setUnitPreference } from '@/lib/units.js';
import { clearWarmCache } from '@/lib/warmCache.js';

/**
 * Auth + the parts of the user document the whole app needs.
 *
 * Anonymous sign-in is deliberately absent: a deletion request has to be
 * answerable, which requires an account (ARCHITECTURE §2.1).
 */
interface AuthState {
  user: User | null;
  userDoc: UserDoc | null;
  loading: boolean;
  error: string | null;
  init(): void;
  refreshUserDoc(): Promise<void>;
  signInGoogle(): Promise<void>;
  signInApple(): Promise<void>;
  sendEmailLink(email: string): Promise<void>;
  /** `link` defaults to the current page; native passes the URL that opened the app. */
  completeEmailLink(link?: string): Promise<boolean>;
  logout(): Promise<void>;
}

const EMAIL_KEY = 'erectus.emailForSignIn';

/**
 * Popup or redirect?
 *
 * iOS Safari partitions storage for third-party origins, and a PWA launched
 * from the home screen has no popup to speak of. A popup there fails with
 * "missing initial state", because the auth handler cannot read back the state
 * it wrote. Redirect is the reliable path on mobile; popup stays on desktop,
 * where it keeps the user in place.
 *
 * The other half of this fix is `authDomain`: it must be the app's own origin
 * (posture-fe600.web.app, or erectus.app when opened there — lib/firebase.ts
 * authDomainFor), which Firebase Hosting serves the handler from, so nothing
 * is cross-origin in the first place.
 */
function prefersRedirect(): boolean {
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const ios = /iP(hone|ad|od)/.test(navigator.userAgent);
  const mobileSafari = ios || (/Safari/.test(navigator.userAgent) && /Mobile/.test(navigator.userAgent));
  return standalone || mobileSafari;
}

/** Errors that mean "the popup route will not work here; use a redirect". */
const POPUP_UNSUPPORTED = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
  'auth/missing-or-invalid-nonce',
  'auth/internal-error',
]);

async function startOAuth(
  provider: AuthProvider,
  fail: (message: string) => void,
): Promise<void> {
  const { auth } = firebase();
  // A redirect that cannot start (unauthorized domain, offline) rejects here;
  // without the catch the button just does nothing.
  const redirect = (): Promise<void> =>
    signInWithRedirect(auth, provider).catch((error: unknown) => fail(authMessage(error)));
  if (prefersRedirect()) {
    await redirect();
    return;
  }
  try {
    await signInWithPopup(auth, provider);
  } catch (error) {
    const code = (error as { code?: string }).code ?? '';
    if (POPUP_UNSUPPORTED.has(code)) {
      await redirect();
      return;
    }
    fail(authMessage(error));
  }
}

/**
 * Native sign-in. Google refuses OAuth inside an embedded WebView
 * (`disallowed_useragent`), so the native SDK runs the account picker and
 * hands back an ID token; the JS SDK signs in with it. `skipNativeAuth` keeps
 * the native Firebase Auth out of it — the JS SDK stays the single session.
 */
async function nativeSignIn(provider: 'google' | 'apple'): Promise<void> {
  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
  const { auth } = firebase();
  if (provider === 'google') {
    const { credential } = await FirebaseAuthentication.signInWithGoogle();
    if (!credential?.idToken) throw Object.assign(new Error('no id token'), { code: 'auth/internal-error' });
    await signInWithCredential(auth, GoogleAuthProvider.credential(credential.idToken));
    return;
  }
  const { credential } = await FirebaseAuthentication.signInWithApple();
  if (!credential?.idToken) throw Object.assign(new Error('no id token'), { code: 'auth/internal-error' });
  await signInWithCredential(
    auth,
    new OAuthProvider('apple.com').credential({
      idToken: credential.idToken,
      ...(credential.nonce ? { rawNonce: credential.nonce } : {}),
    }),
  );
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  userDoc: null,
  loading: true,
  error: null,

  init() {
    const { auth } = firebase();
    // Completes a redirect sign-in started before the page reloaded. Native
    // never redirects, and has no resolver for this call.
    if (!isNative()) {
      void getRedirectResult(auth).catch((error) => {
        set({ error: authMessage(error) });
      });
    }
    /**
     * Offline on a phone, Auth can take a while to resolve from persisted
     * state — and a spinner that never ends looks like a broken app. Fall
     * through to the signed-out screen, which at least explains itself.
     */
    const bootTimeout = window.setTimeout(() => {
      if (get().loading) set({ loading: false });
    }, 6000);

    onAuthStateChanged(auth, async (user) => {
      window.clearTimeout(bootTimeout);
      setCrashUser(user?.uid ?? null);
      if (!user) {
        set({ user: null, userDoc: null, loading: false });
        return;
      }
      const userDoc = await getUser(user.uid).catch(() => null);
      const units = userDoc?.settings?.units;
      if (units) setUnitPreference(units);
      set({ user, userDoc, loading: false });
      void refreshPushToken(user.uid);
      setProps({
        platform: platform(),
        has_data_optin: userDoc?.consent?.dataOptIn ? 'true' : 'false',
        ...(userDoc?.gamification
          ? { stage: String(userDoc.gamification.stage) as '1' | '2' | '3' | '4' | '5' }
          : {}),
        ...(userDoc?.settings
          ? { reminder_days: String(userDoc.settings.reminderEveryDays) as '3' | '7' }
          : {}),
      });
    });
  },

  async refreshUserDoc() {
    const user = get().user;
    if (!user) return;
    const userDoc = await getUser(user.uid).catch(() => null);
    set({ userDoc });
  },

  async signInGoogle() {
    set({ error: null });
    if (isNative()) {
      await nativeSignIn('google').catch((error) => set({ error: authMessage(error) }));
      return;
    }
    await startOAuth(googleProvider, (message) => set({ error: message }));
  },

  async signInApple() {
    set({ error: null });
    if (isNative()) {
      await nativeSignIn('apple').catch((error) => set({ error: authMessage(error) }));
      return;
    }
    await startOAuth(appleProvider, (message) => set({ error: message }));
  },

  async sendEmailLink(email: string) {
    set({ error: null });
    try {
      await sendSignInLinkToEmail(firebase().auth, email, {
        // Native's origin (capacitor://localhost, https://localhost) is not a
        // real address. The hosted origin is, and its /.well-known files hand
        // the link back to the app (lib/appLinks.ts).
        url: `${isNative() ? `https://${readEnv().authDomain}` : window.location.origin}/login`,
        handleCodeInApp: true,
      });
      window.localStorage.setItem(EMAIL_KEY, email);
    } catch (error) {
      set({ error: authMessage(error) });
    }
  },

  async completeEmailLink(link = window.location.href) {
    const { auth } = firebase();
    if (!isSignInWithEmailLink(auth, link)) return false;
    const stored = window.localStorage.getItem(EMAIL_KEY);
    const email = stored ?? window.prompt('Confirm the email you used to sign in') ?? '';
    if (!email) return false;
    try {
      await signInWithEmailLink(auth, email, link);
      window.localStorage.removeItem(EMAIL_KEY);
      return true;
    } catch (error) {
      set({ error: authMessage(error) });
      return false;
    }
  },

  async logout() {
    const uid = get().user?.uid;
    // Unregister this device first: after signOut the rules no longer let us
    // write the user doc, and the account's reminders would keep arriving here.
    if (uid) await forgetPushToken(uid).catch(() => undefined);
    if (isNative()) {
      // Clears the native Google account choice so the next sign-in can pick another.
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      await FirebaseAuthentication.signOut().catch(() => undefined);
    }
    await signOut(firebase().auth);
    clearWarmCache();
    set({ user: null, userDoc: null });
  },
}));

function authMessage(error: unknown): string {
  const code = (error as { code?: string }).code ?? '';
  if (code === 'auth/popup-closed-by-user') return 'Sign-in window was closed.';
  // Native plugins report a dismissed account picker as an error with no auth/ code.
  if (/cancel/i.test(String((error as { message?: string }).message ?? ''))) {
    return 'Sign-in was cancelled.';
  }
  if (code === 'auth/network-request-failed') return 'No connection. Try again.';
  if (code === 'auth/invalid-email') return 'That email address does not look right.';
  if (code === 'auth/unauthorized-domain') {
    return 'This address is not an approved sign-in domain for the project.';
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return 'You already have an account with this email through another sign-in method.';
  }
  if (code === 'auth/operation-not-allowed') {
    // The provider exists in the SDK but is not enabled on the project. Say so
    // plainly instead of "try again", which would be a lie.
    return 'That sign-in method is not enabled for this app yet. Use Google instead.';
  }
  return 'Sign-in failed. Please try again.';
}

/** What the app still needs from this user before the main flow can run. */
export type OnboardingGap = 'consent' | 'profile' | null;

export function onboardingGap(userDoc: UserDoc | null): OnboardingGap {
  if (!userDoc?.consent) return 'consent';
  if (userDoc.consent.tosVersion !== CONSENT_VERSIONS.tos) return 'consent';
  if (userDoc.consent.privacyVersion !== CONSENT_VERSIONS.privacy) return 'consent';
  if (userDoc.consent.chdpVersion !== CONSENT_VERSIONS.chdp) return 'consent';
  if (!userDoc.profile) return 'profile';
  if (!userDoc.onboardingCompletedAt) return 'profile';
  return null;
}

export function age(birthYear: number, now = new Date()): number {
  return now.getFullYear() - birthYear;
}

export function canCreateAccount(birthYear: number): boolean {
  return age(birthYear) >= AGE_GATES.minimumAccountAge;
}

export function canOptInToDataSharing(birthYear: number | undefined): boolean {
  if (birthYear === undefined) return false;
  return age(birthYear) >= AGE_GATES.dataOptInAge;
}
